/* @flow */

import React from 'react';
import colorUtils from 'color';
import classNames from 'classnames';
import { isFunction } from 'lodash';
import useSheet from 'app/jss';

import FormLabel from './FormLabel';

type Props = {
  classes: Object,
  defaultText?: string,
  disabled?: boolean,
  label?: string,
  lightContent?: boolean,
  name: string,
  uppercase?: boolean,
  onBlur?: Function,
  onChange: Function,
  options: Array<*>,
  required?: boolean,
  size?: string,
  value?: string | number
};

const defaultProps = {
  defaultText: '',
  disabled: false,
  label: undefined,
  lightContent: false,
  uppercase: true,
  onBlur: undefined,
  required: false,
  size: 'default',
  value: ''
};

function SelectField({
  classes,
  defaultText,
  disabled,
  label,
  lightContent,
  name,
  onBlur,
  onChange,
  options,
  required,
  size,
  uppercase,
  value
}: Props) {
  return (
    <div className="form-field-group">
      {label && (
        <FormLabel
          htmlFor={name}
          lightContent={lightContent}
          required={required}
          uppercase={uppercase}
        >
          {label}
        </FormLabel>
      )}
      <select
        className={classNames(classes.selectField, size, {
          disabled,
          light: lightContent,
          placeholder: !value
        })}
        disabled={disabled}
        name={name}
        value={value}
        onBlur={event => (isFunction(onBlur) ? onBlur(event.target.value) : undefined)}
        onChange={event => onChange(event.target.value)}
      >
        <option value="" disabled>
          {defaultText}
        </option>
        {options.map(opt =>
          typeof opt === 'object' ? (
            <option key={opt.value} value={opt.value} disabled={!!opt.disabled}>
              {opt.label}
            </option>
          ) : (
            <option key={opt} value={opt}>
              {opt}
            </option>
          )
        )}
      </select>
    </div>
  );
}

SelectField.defaultProps = defaultProps;

function applyStyles(theme: Theme) {
  return {
    selectField: {
      appearance: 'none',
      backgroundColor: theme.color.basis2,
      border: 0,
      borderRadius: theme.borderRadius.default,
      color: theme.color.basis6,
      display: 'block',
      flexGrow: 1,
      fontSize: theme.fontSize.large,
      fontWeight: theme.fontWeight.demiBold,
      padding: theme.spacing.small,
      width: '100%',
      transition: 'background-color 0.2s, color 0.2s',
      '&.placeholder': {
        color: 'rgba(255,255,255,0.2)'
      },
      '&.small': {
        fontSize: theme.fontSize.medium
      },
      '&.light': {
        backgroundColor: theme.color.basis6,
        color: theme.color.basis1,
        '&.placeholder': {
          color: 'rgba(0,0,0,0.2)'
        }
      },
      '&[disabled]': {
        backgroundColor: colorUtils(theme.color.basis4)
          .alpha(0.5)
          .string(),
        color: theme.color.basis4
      },
      '&.light[disabled]': {
        backgroundColor: colorUtils(theme.color.basis4)
          .alpha(0.5)
          .string(),
        color: theme.color.basis4
      },
      '&:focus': {
        backgroundColor: theme.color.basis3,
        color: theme.color.basis5
      },
      '&.light:focus': {
        backgroundColor: theme.color.basis5,
        color: theme.color.basis2
      }
    }
  };
}

export default useSheet(applyStyles)(SelectField);
